import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../api.ts';
import { formatEuros } from '../../../format.ts';
import { fmtDay, type Dependence, type Overview } from './types.ts';

function DependenceCard({ dependence: d }: { dependence: Dependence }) {
  const share = d.top?.sharePct ?? 0;
  const over = d.status === 'EXCEEDED' || d.status === 'BLOCKED';
  const tone = over ? 'var(--danger)' : d.status === 'WATCH' ? 'var(--warning)' : 'var(--success)';

  let message: string;
  if (d.status === 'NOT_EVALUATED') {
    message = `Pas encore évaluée : la règle s'applique à partir de ${formatEuros(d.minRevenueCents)} de chiffre d'affaires sur 12 mois (vous en êtes à ${formatEuros(d.totalCents)}).`;
  } else if (d.status === 'OK') {
    message = 'Votre clientèle est bien répartie.';
  } else if (d.status === 'WATCH') {
    message = `Vigilance : ${d.top?.name} représente ${Math.round(share)} % de votre chiffre d'affaires. Le plafond est de ${d.thresholdPct} %.`;
  } else if (d.status === 'EXCEEDED') {
    message = d.enforced
      ? `${d.top?.name} représente ${Math.round(share)} % de votre chiffre d'affaires, au-delà de ${d.thresholdPct} %. Vous avez jusqu'au ${fmtDay(d.graceEndsAt!)} pour rééquilibrer : développez d'autres clients et ajoutez vos ventes hors app. Passé ce délai, vous ne pourrez plus candidater chez ce client.`
      : `${d.top?.name} représente ${Math.round(share)} % de votre chiffre d'affaires, au-delà de ${d.thresholdPct} %. Pensez à diversifier votre clientèle.`;
  } else {
    message = `${d.top?.name} représente ${Math.round(share)} % de votre chiffre d'affaires. Vous ne pouvez plus candidater chez ce client tant que cette part n'est pas redescendue sous ${d.thresholdPct} %. Les autres établissements restent accessibles.`;
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontWeight: 800 }}>Répartition de ma clientèle</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>12 derniers mois, à l'encaissement. Aucun client ne doit dépasser {d.thresholdPct} % de votre chiffre d'affaires.</div>
      {d.top && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
            <span>{d.top.name}</span>
            <span style={{ color: tone }}>{Math.round(share)} %</span>
          </div>
          <div style={{ position: 'relative', height: 10, background: 'var(--bg)', borderRadius: 99, overflow: 'visible' }} role="img" aria-label={`Part du client principal : ${Math.round(share)} %`}>
            <div style={{ width: `${Math.min(100, share)}%`, height: '100%', background: tone, borderRadius: 99 }} />
            <div title={`Plafond ${d.thresholdPct} %`} style={{ position: 'absolute', left: `${d.thresholdPct}%`, top: -3, bottom: -3, width: 2, background: 'var(--text)' }} />
          </div>
        </div>
      )}
      <div style={{ fontSize: 13, lineHeight: 1.5, color: over ? 'var(--danger)' : 'var(--text-secondary)', fontWeight: over ? 700 : 500 }}>{message}</div>
    </div>
  );
}

export function BusinessHome() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<Overview>('/extra/overview').then(setOverview);
  }, []);
  useEffect(load, [load]);

  async function attest() {
    setBusy(true);
    try {
      await api.post('/extra/attestation', { accept: true });
      setAgree(false);
      load();
    } finally {
      setBusy(false);
    }
  }

  if (!overview) return <div className="empty-state">Chargement…</div>;
  const { attestation, billing, urssaf } = overview;
  const todo = urssaf.toDeclare;

  return (
    <>
      {!attestation.valid && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10, borderColor: 'var(--danger)' }}>
          <div style={{ fontWeight: 800, color: 'var(--danger)' }}>
            {attestation.reason === 'EXPIRED' ? 'Votre attestation sur l’honneur a expiré' : 'Attestation sur l’honneur requise'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Sans attestation à jour, vous ne pouvez pas candidater à une mission.
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, background: 'var(--bg)', padding: 12, borderRadius: 'var(--radius-md)' }}>{attestation.text}</div>
          <label style={{ display: 'flex', gap: 8, fontSize: 13, fontWeight: 600, alignItems: 'flex-start' }}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
            J'atteste sur l'honneur l'exactitude de cette déclaration.
          </label>
          <button className="btn btn-primary" disabled={!agree || busy} onClick={attest}>Je l'atteste</button>
        </div>
      )}
      {attestation.valid && attestation.expiresAt && (
        <div className="badge badge-available" style={{ padding: '10px 14px', fontSize: 12.5 }}>
          Attestation « plus d'un client » à jour, à renouveler avant le {fmtDay(attestation.expiresAt)}.
        </div>
      )}

      <DependenceCard dependence={overview.dependence} />

      {(!billing.complete || !billing.mandateAccepted || billing.pendingInvoices > 0) && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontWeight: 800 }}>Mes factures</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {!billing.complete
              ? 'Complétez votre profil de facturation (SIRET, adresse, TVA) pour que vos factures soient établies à votre nom.'
              : !billing.mandateAccepted
                ? 'Acceptez le mandat de facturation pour que Mon Extra établisse vos factures de mission à votre nom.'
                : `${billing.pendingInvoices} facture(s) de mission en attente d'émission.`}
          </div>
          <Link to="/extra/activite/facturation" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>
            {billing.complete && billing.mandateAccepted ? 'Voir mes factures' : 'Compléter'}
          </Link>
        </div>
      )}

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontWeight: 800 }}>Déclaration URSSAF</div>
        {todo ? (
          <>
            <div className={todo.status === 'OVERDUE' ? 'badge badge-unavailable' : 'badge badge-pending'} style={{ padding: '10px 14px', fontSize: 12.5, lineHeight: 1.5 }}>
              {todo.status === 'OVERDUE' ? 'En retard : ' : 'À déclarer : '}
              {todo.label}, {formatEuros(todo.declarableCents)} avant le {fmtDay(todo.deadline)}.
            </div>
            <Link to="/extra/activite/urssaf" className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>Préparer ma déclaration</Link>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Rien à déclarer pour l'instant.
              {urssaf.current && ` ${urssaf.current.label} : ${formatEuros(urssaf.current.declarableCents)} encaissés, à déclarer avant le ${fmtDay(urssaf.current.deadline)}.`}
            </div>
            <Link to="/extra/activite/urssaf" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>Voir mes périodes</Link>
          </>
        )}
      </div>

      <label className="card" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.5, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={overview.emailReminders}
          onChange={async (e) => {
            const emailReminders = e.target.checked;
            setOverview({ ...overview, emailReminders });
            await api.put('/extra/preferences', { emailReminders });
          }}
          style={{ marginTop: 3 }}
        />
        <span>
          <strong>Rappels par e-mail</strong><br />
          Échéance de déclaration URSSAF, attestation à renouveler, clientèle à rééquilibrer. Un seul message par sujet.
        </span>
      </label>
    </>
  );
}
